import { Moment } from 'moment';
import { useCallback, useState } from 'react';
import { ResourceManagmentApi } from 'api/resource-management';
import { toastService } from 'components/molecules/toast';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { FilterGroupType } from 'components/pages/types';
import { useTranslate } from 'hooks/use-translation';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import { GroupBy } from '../types';
import { asApiGroupBy, asApiZoomType } from '../utils/common';

interface Props {
  groupBy: GroupBy;
  viewPreset: ViewPresetId;
  filter: GroupedFilter;
  defaultGroup: FilterGroupType;
}

interface Result {
  exportModalVisibility: boolean;
  showExportModal: () => void;
  hideExportModal: () => void;
  onExport: (startDate: Moment, endDate: Moment) => Promise<any>;
}

export const useControlExport = ({
  groupBy,
  viewPreset,
  filter,
}: Props): Result => {
  const translate = useTranslate();
  const [exportModalVisibility, setExportModalVisibility] = useState(false);
  const showExportModal = useCallback(() => setExportModalVisibility(true), []);
  const hideExportModal = useCallback(
    () => setExportModalVisibility(false),
    [],
  );
  const onExport = useCallback(
    (startDate: Moment, endDate: Moment) => {
      return new Promise<void>((resolve, reject) =>
        ResourceManagmentApi.exportAllocations({
          startDate,
          endDate,
          groupBy: asApiGroupBy(groupBy),
          zoomType: asApiZoomType(viewPreset),
          filter: {
            ...filter,
            groupCriteria: filter.groupCriteria.filter((x) =>
              FieldFilter.isValid(x.criteria),
            ),
          },
        })
          .then((downloadUrl) => {
            window.open(downloadUrl, '_blank');
            resolve();
          })
          .catch(() => {
            toastService({
              title: translate('project.exports.error.title'),
              message: translate('project.exports.error.message'),
            }).error();
            reject();
          }),
      );
    },
    [groupBy, viewPreset, filter, translate],
  );

  return {
    exportModalVisibility,
    showExportModal,
    hideExportModal,
    onExport,
  };
};
